/**
 * Application shell: renderer, clock, camera controller, toolbar, time bar, URL state, follow mode and
 * view switching. Views (Earth, Moon, …) are loaded on demand and implement `View` (./View.ts).
 */
import '../styles/shared';
import '../styles.css';
import {
  QUAT_IDENTITY,
  quatConjugate,
  quatMultiply,
  quatNormalize,
  quatRotate,
  type Quat,
} from '../astro/quat';
import { SimClock } from '../astro/time';
import { DEG_TO_RAD } from '../astro/constants';
import { add, cross, dot, length, normalize, scale, sub, type Vec3 } from '../astro/vec3';
import { letterCode, QuaternionOrbitControls } from '../camera/QuaternionOrbitControls';
import {
  heightAboveKm,
  keepOutside,
  keepOutsideByTranslation,
  orbitStateLookingFrom,
  type OrbitLimits,
  type OrbitState,
} from '../camera/orbitMath';
import { GuardianGrid } from '../render/GuardianGrid';
import { I18n, detectLang, type Lang, type MessageKey } from '../i18n';
import { Renderer, WebGLUnavailableError } from '../render/Renderer';
import { SkyMesh } from '../render/SkyMesh';
import { createStarfield } from '../render/starfield';
import { configureKtx2 } from '../render/textures';
import { loadManifest } from '../data/loader';
import { About } from '../ui/About';
import { SettingsDialog } from '../ui/SettingsDialog';
import { FavoritesDialog } from '../ui/FavoritesDialog';
import { watchKeyboardLayout } from '../ui/keyboardLayout';
import { ReportDialog, qualitySummary } from '../ui/ReportDialog';
import { setSimTime } from '../render/simTime';
import { setPreviewFrameRate } from '../ui/ModelPreview';
import { MusicPanel } from '../ui/MusicPanel';
import { TimeControl } from '../ui/TimeControl';
import { loadZoneSetting } from '../i18n/timeZone';
import { Altimeter } from '../ui/Altimeter';
import { Toolbar, VIEW_LABELS } from '../ui/Toolbar';
import { h, setSheetGrabLabel } from '../ui/dom';
import { copyCanvas, downloadPng, drawLabels, hideOverlays } from '../render/photo';
import { MAX_ABS_RATE, parseUrlState, serializeUrlState, type FrameMode, type ViewId } from './urlState';
import {
  readDeviceSignals,
  resolveQuality,
  setQuality,
  storedPixelRatio,
  storeChoice,
  storePixelRatio,
} from '../render/quality';
import {
  UpdateWatcher,
  claimAutoReload,
  saveCamera,
  takeCamera,
  type CameraSnapshot,
  type UpdateKind,
} from './updates';
import type { FollowApi, View, ViewFactory, ViewHost } from './View';
import type { Engine } from '../engine';
import { EmbedApi, engineIdOf, type EmbedState, type ObserverPlace, type SetParams } from './embedApi';

const UI_REFRESH_S = 0.25;
/** A pointer that moved less than this between down and up is a click, not a drag. */
const CLICK_TOLERANCE_PX = 5;
const FOLLOW_MIN_DISTANCE_KM = 10;
/** The camera stays this far above any body's surface (2 m: below a rover's mast). */
const CAMERA_CLEARANCE_KM = 0.002;

const VIEW_LOADERS: Record<ViewId, () => Promise<ViewFactory>> = {
  earth: () => import('../earth/EarthView').then((m) => m.createEarthView),
  moon: () => import('../moon/MoonView').then((m) => m.createMoonView),
  mars: () => import('../mars/MarsView').then((m) => m.createMarsView),
  solar: () => import('../solar/SolarView').then((m) => m.createSolarView),
};

/**
 * Offline support (production builds only: the dev server's modules are not cacheable assets). New deployments
 * are detected by the UpdateWatcher (./updates.ts), not by the worker: its script rarely changes.
 */
/**
 * Phone portrait layout (bottom sheets); must match the media query in styles.css. Landscape phones keep side
 * panels, which do not cover the bottom of the view.
 */
const PHONE_QUERY = '(max-width: 640px)';

function registerServiceWorker(baseUrl: string): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const hadController = navigator.serviceWorker.controller !== null;
  // First visit: resources fetched before the worker controls the page bypass it, and Chromium keeps routing
  // requests past it for a moment even after `controllerchange`. Hand everything loaded so far over for caching
  // once control starts, then again a little later; URLs already cached are skipped by the worker.
  const post = (): void => {
    const urls = performance
      .getEntriesByType('resource')
      .map((e) => e.name)
      .filter((u) => u.startsWith(window.location.origin));
    navigator.serviceWorker.controller?.postMessage({
      type: 'cache-urls',
      urls: [...urls, window.location.href],
    });
  };
  let handedOver = false;
  const handOver = (): void => {
    if (handedOver) return;
    handedOver = true;
    post();
    for (const delayMs of [3_000, 12_000]) window.setTimeout(post, delayMs);
  };
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) handOver();
  });
  const register = (): void => {
    navigator.serviceWorker
      .register(`${baseUrl}sw.js`, { scope: baseUrl })
      .then(() => navigator.serviceWorker.ready)
      .then(() => {
        // Already controlled without a change event (claimed before this listener ran).
        if (!hadController && navigator.serviceWorker.controller) handOver();
      })
      .catch((err: unknown) => {
        console.warn('Service worker registration failed', err);
      });
  };
  // Register after the page's own loads, so caching never competes with the first render.
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

function main(): void {
  const root = document.getElementById('app');
  if (!root) throw new Error('#app not found');
  const app: HTMLElement = root;

  const startParams = new URLSearchParams(window.location.search);
  const url = parseUrlState(window.location.search);
  // Dates are shown in the computer's time zone unless the visitor picked another one (time bar).
  const i18n = new I18n(detectLang(url.lang ?? null, navigator.languages), loadZoneSetting());
  let explicitLang: Lang | undefined = url.lang;
  let frame: FrameMode = url.frame;
  let viewId: ViewId = url.view;
  // Embed mode (?embed=1, for an iframe): the 3D view alone, with the parts listed in `ui` (panel, time) and a
  // link to the full app. The embed API (./embedApi.ts) answers whenever the page is framed.
  const embed = startParams.get('embed') === '1';
  const embedUi = new Set((startParams.get('ui') ?? '').split(','));
  document.documentElement.classList.toggle('embed', embed);
  document.documentElement.classList.toggle('embed-panel', embed && embedUi.has('panel'));
  document.documentElement.classList.toggle('embed-time', embed && embedUi.has('time'));

  // Canonical URL: language and view only (as in public/sitemap.xml), so the countless shared links with a
  // selection, a time or filters fold into one page per view and language.
  function updateCanonical(): void {
    const query = `?lang=${i18n.lang}${viewId === 'earth' ? '' : `&view=${viewId}`}`;
    const href = new URL(query, window.location.origin + import.meta.env.BASE_URL).href;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', href);
  }

  const applyDocumentLang = (): void => {
    document.documentElement.lang = i18n.lang;
    document.title = i18n.t('app.title');
    // Search engines render the page: description and canonical URL follow the language shown.
    document.querySelector('meta[name="description"]')?.setAttribute('content', i18n.t('app.description'));
    updateCanonical();
    document.querySelector('canvas')?.setAttribute('aria-label', i18n.t('app.canvasLabel'));
    setSheetGrabLabel(i18n.t('panel.collapse'));
  };
  applyDocumentLang();
  i18n.onChange(applyDocumentLang);

  const viewport = app.querySelector<HTMLElement>('#viewport');
  if (!viewport) throw new Error('#viewport not found');

  // Quality tier (src/app/quality.ts): decided before the renderer, which it configures.
  const qualityState = resolveQuality(startParams.get('quality'), readDeviceSignals());
  setQuality(qualityState);
  const q = qualityState.settings;
  document.documentElement.dataset['quality'] = q.tier;
  setPreviewFrameRate(q.previewFps);
  // Idle: clock paused, camera still, no input for a moment (the low tier then draws fewer frames).
  let lastInputMs = performance.now();
  for (const type of ['pointerdown', 'pointermove', 'wheel', 'keydown', 'touchstart']) {
    window.addEventListener(type, () => (lastInputMs = performance.now()), { passive: true, capture: true });
  }
  let isIdle = (): boolean => false;

  let renderer: Renderer;
  try {
    renderer = new Renderer(viewport, {
      logarithmicDepthBuffer: true,
      antialias: q.msaa,
      maxPixelRatio: q.maxPixelRatio,
      minPixelRatio: q.minPixelRatio,
      startPixelRatio: storedPixelRatio(q.tier),
      maxFps: q.maxFps,
      idleFps: q.idleFps,
      isIdle: () => isIdle(),
      onPixelRatio: (ratio) => storePixelRatio(q.tier, ratio),
    });
    configureKtx2(renderer.renderer, import.meta.env.BASE_URL);
    renderer.canvas.setAttribute('aria-label', i18n.t('app.canvasLabel'));
  } catch (err) {
    if (err instanceof WebGLUnavailableError) {
      viewport.textContent = i18n.t('app.webglUnavailable');
      return;
    }
    throw err;
  }

  const clock = new SimClock();
  if (url.time) clock.jumpTo(url.time);
  clock.setRate(url.rate);

  // Real sky (NASA SVS star map); the procedural starfield shows until it has loaded.
  const stars = createStarfield(4000, renderer.pixelRatio);
  const sky = new SkyMesh({
    baseUrl: import.meta.env.BASE_URL,
    maxTextureSize: renderer.maxTextureSize,
    anisotropy: renderer.renderer.capabilities.getMaxAnisotropy(),
    onReady: () => {
      stars.visible = false;
      // The 8k level (~25 MB) only pays off on large high-density screens of the high tier, once the page has
      // settled.
      const px = Math.max(window.screen.width, window.screen.height) * window.devicePixelRatio;
      if (q.textures8k && px >= 2500 && renderer.maxTextureSize >= 8192)
        window.setTimeout(() => sky.requestDetail(), 8000);
    },
  });
  renderer.scene.add(sky.mesh, stars);
  // Faces of the cube the camera cannot leave, shown as it comes near one (src/render/GuardianGrid.ts).
  const guardian = new GuardianGrid();
  renderer.scene.add(guardian.group);

  const placeholderHome: OrbitState = orbitStateLookingFrom([0, 0, 0], [1, 0, 0], [0, 0, 1], 20_000);
  const controls = new QuaternionOrbitControls(renderer.canvas, {
    limits: { minDistanceKm: 1, maxDistanceKm: 1e7 },
    home: placeholderHome,
    fovYRad: (renderer.camera.fov * Math.PI) / 180,
  });
  isIdle = () => clock.paused && !controls.moving && performance.now() - lastInputMs > 1500;

  let view: View | undefined;
  // The visitor's place, sent by an embedding page (embed API); kept across view switches, never in the URL.
  let observer: ObserverPlace | undefined;
  let viewDom: HTMLElement[] = [];
  let panelToggle: (() => boolean) | undefined;

  const sceneFromInertial = (date: Date): Quat =>
    view && frame === 'fixed' ? quatConjugate(view.bodyOrientation(date)) : QUAT_IDENTITY;

  const homeState = (date: Date): OrbitState => {
    if (!view) return placeholderHome;
    const dir = quatRotate(
      quatMultiply(sceneFromInertial(date), view.bodyOrientation(date)),
      view.homeDirectionBody,
    );
    return orbitStateLookingFrom([0, 0, 0], dir, [0, 0, 1], view.homeDistanceKm);
  };

  // URL sync (on user actions only, never per frame).
  const syncUrl = (): void => {
    const search = serializeUrlState(
      {
        view: viewId,
        lang: explicitLang,
        frame,
        time: clock.isLive() ? undefined : clock.nowUtc(),
        rate: clock.rate,
      },
      (p) => {
        view?.writeUrl(p);
        // Test hooks, a forced quality tier and the perf overlay survive the URL rewrites (and update reloads).
        if (startParams.has('e2e')) p.set('e2e', '');
        for (const key of ['quality', 'debug', 'embed', 'ui']) {
          const value = startParams.get(key);
          if (value) p.set(key, value);
        }
      },
    );
    history.replaceState(null, '', `${window.location.pathname}${search}${window.location.hash}`);
    updateEmbedLink();
    embedApi?.changed();
  };

  // The full app showing the same thing (the embed's corner link and the API's `url`).
  const fullAppUrl = (): string => {
    const p = new URLSearchParams(window.location.search);
    for (const key of ['embed', 'ui', 'e2e']) p.delete(key);
    const query = p.toString();
    return new URL(`${import.meta.env.BASE_URL}${query ? `?${query}` : ''}`, window.location.origin).href;
  };
  const embedLink = h('a', { class: 'embed-credit', target: '_blank', rel: 'noopener' });
  const embedRecenter = h('button', { type: 'button', class: 'embed-recenter' });
  function updateEmbedLink(): void {
    if (!embed) return;
    embedLink.href = fullAppUrl();
    embedLink.textContent = i18n.t('embed.open');
    embedRecenter.textContent = i18n.t('toolbar.recenter');
    embedRecenter.title = i18n.t('embed.recenter.hint');
  }
  // The link the frame was opened with, without the embedding parameters: "Recenter" returns to it.
  const embedStartQuery = (): string => {
    const p = new URLSearchParams(startParams);
    for (const key of ['embed', 'ui', 'e2e', 'quality', 'debug']) p.delete(key);
    return p.toString();
  };
  let embedApi: EmbedApi | undefined;

  const notice = h('p', { class: 'notice', role: 'status', hidden: true });
  // Short confirmations (photo saved): shown for 3 s.
  const toast = h('p', { class: 'toast', role: 'status', hidden: true });
  let toastTimer = 0;
  const showToast = (text: string): void => {
    toast.textContent = text;
    toast.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => (toast.hidden = true), 3000);
  };
  const offline = h('p', { class: 'offline-badge', role: 'status', hidden: true });
  const renderOffline = (): void => {
    offline.hidden = navigator.onLine;
    offline.textContent = i18n.t('app.offline');
  };
  window.addEventListener('online', renderOffline);
  window.addEventListener('offline', renderOffline);
  i18n.onChange(renderOffline);
  renderOffline();
  registerServiceWorker(import.meta.env.BASE_URL);
  const showNotice = (text: string | undefined): void => {
    notice.hidden = !text;
    notice.textContent = text ?? '';
  };

  // New build or newer data while the tab stays open (./updates.ts): a tab in the background reloads when it
  // comes back; a visible one offers a button. The URL already holds view, selection, filters and time; the
  // camera is carried over in sessionStorage.
  const updateText = h('span');
  const updateButton = h('button', { type: 'button', class: 'btn' });
  const updateNotice = h('p', { class: 'update-notice', role: 'status', hidden: true }, [
    updateText,
    updateButton,
  ]);
  let updateKind: UpdateKind | undefined;
  const renderUpdateNotice = (): void => {
    updateText.textContent = updateKind
      ? i18n.t(updateKind === 'app' ? 'app.updateReady' : 'app.dataReady')
      : '';
    updateButton.textContent = i18n.t('app.refresh');
  };
  i18n.onChange(renderUpdateNotice);
  const cameraSnapshot = (): CameraSnapshot => {
    const s = controls.state;
    const q = s.orientation;
    return {
      view: viewId,
      frame,
      savedAtMs: Date.now(),
      targetKm: [s.targetKm[0], s.targetKm[1], s.targetKm[2]],
      distanceKm: s.distanceKm,
      orientation: { x: q.x, y: q.y, z: q.z, w: q.w },
      following: controls.following,
    };
  };
  const reloadKeepingView = (): void => {
    syncUrl();
    if (view) saveCamera(cameraSnapshot());
    window.location.reload();
  };
  updateButton.addEventListener('click', reloadKeepingView);
  let reloadWhenVisible = false;
  document.addEventListener('visibilitychange', () => {
    if (reloadWhenVisible && document.visibilityState === 'visible') reloadKeepingView();
  });
  // Embed mode shows no notice (a classroom screen: nobody clicks): the frame reloads by itself once nobody has
  // touched it for a while, so someone turning the view is not interrupted. The embedding page gets `ready` again.
  // In e2e, 5 s: software WebGL on CI runners can stall the page for over a second between two input events.
  const embedIdleMs = startParams.has('e2e') ? 5000 : 3 * 60_000;
  let idleReload = 0;
  const reloadWhenIdle = (): void => {
    const waitMs = lastInputMs + embedIdleMs - performance.now();
    if (waitMs <= 0) reloadKeepingView();
    else idleReload = window.setTimeout(reloadWhenIdle, waitMs);
  };
  const updates = new UpdateWatcher({
    baseUrl: import.meta.env.BASE_URL,
    commit: __PERIGEE_BUILD__.commit,
    loaded: () => view?.dataVersions?.() ?? new Map<string, string>(),
    loadManifest: () => loadManifest(import.meta.env.BASE_URL),
    onUpdate: (kind) => {
      if (document.visibilityState === 'hidden') {
        reloadWhenVisible = true;
        return;
      }
      if (embed) {
        if (!idleReload) reloadWhenIdle();
        return;
      }
      updateKind = kind;
      renderUpdateNotice();
      updateNotice.hidden = false;
    },
  });
  updates.start();
  if (startParams.has('e2e')) {
    Object.assign(window, {
      __perigeeShell: { checkUpdates: () => updates.check(), camera: () => controls.state },
    });
  }

  /**
   * Camera limits of the current view: its distances, the cube enclosing its sphere of motion (the camera never
   * leaves it), and the closest approach around a pivot moved away from the body (Alt + drag).
   */
  const cameraLimits = (limits: OrbitLimits): OrbitLimits => ({
    ...limits,
    boundsHalfKm: view?.limits.maxDistanceKm ?? limits.maxDistanceKm,
    pivotMinDistanceKm: FOLLOW_MIN_DISTANCE_KM,
  });

  // Follow mode, shared by all views.
  let followEnd: (() => void) | undefined;
  /** Surface radius of the followed object (0 for a spacecraft), for the altimeter. */
  let followSurfaceKm = 0;
  const endFollow = (): void => {
    const cb = followEnd;
    followEnd = undefined;
    if (view) controls.setLimits(cameraLimits(view.limits));
    cb?.();
  };
  const follow: FollowApi = {
    start(scenePosition, distanceKm, onEnd, options = {}) {
      const pos = scenePosition();
      if (!pos || !view) return false;
      if (controls.following) follow.stop();
      followEnd = onEnd;
      followSurfaceKm = options.surfaceRadiusKm ?? 0;
      // The followed object is the pivot: its own closest approach applies (a 3D model's few metres, a planet's
      // 1.05 radii), not the generic one for a point in space.
      const followMinKm = options.minDistanceKm ?? FOLLOW_MIN_DISTANCE_KM;
      controls.setLimits({
        ...cameraLimits({ minDistanceKm: followMinKm, maxDistanceKm: view.limits.maxDistanceKm }),
        pivotMinDistanceKm: followMinKm,
      });
      controls.setFollow(scenePosition);
      // Look at the object from above (radially outward from the body) unless told otherwise, north up.
      controls.flyTo(orbitStateLookingFrom(pos, options.viewFrom ?? pos, [0, 0, 1], distanceKm));
      return true;
    },
    stop() {
      if (!controls.following) return;
      controls.setFollow(undefined);
      const camKm = controls.cameraPositionKm;
      endFollow();
      if (view) {
        controls.flyTo({
          targetKm: [0, 0, 0],
          distanceKm: Math.max(view.limits.minDistanceKm, length(camKm)),
          orientation: controls.orientation,
        });
      }
    },
    get active() {
      return controls.following;
    },
  };

  const about = new About(i18n, import.meta.env.BASE_URL);
  // Labels of the letter keys for the controls drawn in the settings window (AZERTY, QWERTZ…).
  watchKeyboardLayout();
  const settings = new SettingsDialog(i18n, {
    qualityChoice: qualityState.choice,
    detectedTier: qualityState.detected.tier,
    onQualityChange: (choice) => {
      storeChoice(choice);
      // Applied from startup (MSAA, textures and models are chosen once): reload in place, view and camera kept.
      reloadKeepingView();
    },
  });
  const report = new ReportDialog(i18n);
  // The headless engine (src/engine), loaded on first use: favorites' names and the embed API's details.
  let engine: Promise<Engine> | undefined;
  const getEngine = (): Promise<Engine> =>
    (engine ??= import('../engine').then((m) =>
      m.createEngine({ baseUrl: new URL(import.meta.env.BASE_URL, window.location.origin).href }),
    ));
  const favorites = new FavoritesDialog(i18n, {
    appUrl: () => new URL(import.meta.env.BASE_URL, window.location.origin).href,
    currentLink: () => {
      syncUrl();
      const link = new URL(fullAppUrl());
      if (follow.active && link.searchParams.has('sel')) link.searchParams.set('follow', '1');
      return link.href;
    },
    onOpen: (query) => void openLink(query),
    nameOf: async (v, sel, lang) => {
      const d = await (await getEngine()).describe(engineIdOf(v, sel), lang);
      return typeof d['name'] === 'string' ? d['name'] : undefined;
    },
  });
  const timeControl = new TimeControl(clock, i18n, syncUrl);

  // Phones: the time bar and bottom sheets cover the lower part of the canvas. Their height feeds the CSS
  // (sheets sit above the time bar) and the camera, whose projection centre moves to the visible part.
  const phone = window.matchMedia(PHONE_QUERY);
  // Run before the UI tick writes to the DOM, so these reads use the layout the browser already has.
  // The bars' real extent feeds the CSS, so panels and notices sit between them whatever their layout
  // (--toolbar-bottom: from the top of the window to the top bar's bottom edge, its open menu included, so the
  // panels make room for it; --timebar-space: from the bottom bar's top edge to the bottom of the window).
  const cssPx = new Map<string, string>();
  const setCssPx = (name: string, px: number): void => {
    const value = `${Math.ceil(px)}px`;
    if (cssPx.get(name) === value) return;
    cssPx.set(name, value);
    document.documentElement.style.setProperty(name, value);
  };
  function updateBottomInset(): void {
    // A bar hidden by the embed mode takes no room.
    const timeShown = timeControl.element.offsetParent !== null;
    const timeRect = timeControl.element.getBoundingClientRect();
    setCssPx('--timebar-h', timeShown ? timeRect.height : 0);
    setCssPx('--timebar-space', timeShown ? window.innerHeight - timeRect.top : 0);
    setCssPx('--toolbar-bottom', toolbar.element.getBoundingClientRect().bottom);
    if (!phone.matches) {
      renderer.setBottomInset(0);
      return;
    }
    const canvasRect = renderer.canvas.getBoundingClientRect();
    let top = canvasRect.bottom;
    for (const el of app.querySelectorAll<HTMLElement>('.side-panel, .music.is-open')) {
      if (el.hidden || el.offsetParent === null) continue;
      top = Math.min(top, el.getBoundingClientRect().top);
    }
    // Only sheets count: the thin time bar alone does not justify moving the view.
    renderer.setBottomInset(top < canvasRect.bottom ? canvasRect.bottom - top : 0);
  }
  phone.addEventListener('change', updateBottomInset);
  window.addEventListener('resize', updateBottomInset);
  const toolbar = new Toolbar(i18n, viewId, frame, {
    onViewChange: (id) => void switchView(id),
    // `music` is created below; the menu can only be used once the page is running.
    onSoundtrack: () => music.toggleOpen(),
    onRecenter: () => {
      follow.stop();
      controls.reset();
    },
    onFrameChange: (mode) => changeFrame(mode),
    onLangChange: (lang) => {
      explicitLang = lang;
      i18n.setLang(lang);
      syncUrl();
    },
    onAbout: () => about.open(),
    onReport: () => report.open(),
    onPhoto: () => void takePhoto(),
    onSettings: () => settings.open(),
    onFavorites: () => favorites.open(),
    onTogglePanel: () => {
      if (panelToggle) toolbar.setPanelOpen(panelToggle());
    },
  });
  function changeFrame(mode: FrameMode): void {
    if (mode === frame) return;
    const date = clock.nowUtc();
    if (view) {
      // Re-express the camera in the other frame so the view does not jump.
      const bodyQ = view.bodyOrientation(date);
      const q = mode === 'inertial' ? bodyQ : quatConjugate(bodyQ);
      const s = controls.state;
      controls.setState({
        targetKm: quatRotate(q, s.targetKm),
        distanceKm: s.distanceKm,
        orientation: quatNormalize(quatMultiply(q, s.orientation)),
      });
    }
    frame = mode;
    toolbar.setFrame(mode);
    controls.setHome(homeState(date));
    syncUrl();
  }
  // The panels follow a bar as soon as it changes size (menu opened, date row, new layout level).
  const barObserver = new ResizeObserver(() => updateBottomInset());
  barObserver.observe(toolbar.element);
  barObserver.observe(timeControl.element);
  const music = new MusicPanel(i18n);
  // The altimeter reads (and sets) the height above the surface: the camera distance minus that radius. A camera
  // left aimed elsewhere than the central body (a follow stopped, a test framing) reads a plain distance.
  const altimeterRefKm = (): number =>
    controls.following
      ? followSurfaceKm
      : length(controls.state.targetKm) < 1
        ? (view?.bodyRadiusKm ?? 0)
        : 0;
  const altimeter = new Altimeter(i18n, (valueKm) => controls.zoomTo(valueKm + altimeterRefKm()));
  app.append(
    toolbar.element,
    timeControl.element,
    altimeter.element,
    music.element,
    notice,
    updateNotice,
    offline,
    about.element,
    settings.element,
    favorites.element,
    toast,
    report.element,
  );
  if (embed) {
    embedRecenter.addEventListener('click', () => {
      follow.stop();
      controls.reset();
      void openLink(embedStartQuery());
    });
    app.append(h('div', { class: 'embed-actions' }, [embedRecenter, embedLink]));
    updateEmbedLink();
    i18n.onChange(updateEmbedLink);
  }
  app.querySelector('.loading')?.remove();

  const host: ViewHost = {
    renderer,
    controls,
    clock,
    i18n,
    baseUrl: import.meta.env.BASE_URL,
    initialParams: startParams,
    e2e: startParams.has('e2e'),
    follow,
    frame: () => frame,
    frameObject: (
      pos: Vec3,
      options: {
        tiltRad?: number;
        minDistanceKm?: number;
        targetFraction?: number;
        distanceKm?: number;
      } = {},
    ) => {
      if (!view || keepRestoredCamera) return;
      follow.stop();
      const r = length(pos);
      if (r === 0) return;
      const radial = normalize(pos);
      // Tilt the viewpoint towards the scene north (or any perpendicular when the object is on the pole).
      const tilt = options.tiltRad ?? 0;
      let up: Vec3 = [0, 0, 1];
      if (Math.abs(dot(radial, up)) > 0.98) up = [1, 0, 0];
      const side = normalize(cross(radial, up));
      const toNorth = cross(side, radial);
      const dir = normalize(add(scale(radial, Math.cos(tilt)), scale(toNorth, Math.sin(tilt))));
      const distanceKm = Math.min(
        view.limits.maxDistanceKm,
        options.distanceKm ??
          Math.max(controls.state.distanceKm, r * 1.6, options.minDistanceKm ?? 0, view.limits.minDistanceKm),
      );
      const target = scale(pos, options.targetFraction ?? 0);
      controls.flyTo(orbitStateLookingFrom(target, dir, [0, 0, 1], distanceKm));
    },
    aimAt: (direction: Vec3) => {
      if (!view || keepRestoredCamera) return;
      follow.stop();
      const d = normalize(direction);
      // Look 10° below the direction, towards the scene south: the star sits above the target (the Sun).
      let north: Vec3 = [0, 0, 1];
      if (Math.abs(dot(d, north)) > 0.98) north = [1, 0, 0];
      const up = normalize(sub(north, scale(d, dot(d, north))));
      const tilt = 10 * DEG_TO_RAD;
      const look = add(scale(d, Math.cos(tilt)), scale(up, -Math.sin(tilt)));
      const { targetKm, distanceKm } = controls.state;
      controls.flyTo(orbitStateLookingFrom(targetKm, scale(look, -1), up, distanceKm));
    },
    sceneFromInertial,
    mount: (el) => {
      viewDom.push(el);
      app.insertBefore(el, timeControl.element);
    },
    syncUrl,
    showNotice,
    setPanelToggle: (labelKey: MessageKey | undefined, toggle?: () => boolean, initiallyOpen = false) => {
      panelToggle = toggle;
      toolbar.setPanel(labelKey, initiallyOpen);
    },
  };

  // Camera carried over an update reload: the view's startup framing (selection from the URL) must not move it,
  // until the user takes the controls. Following resumes once the selected object has a position.
  let keepRestoredCamera = false;
  let resumeFollowUntilMs = 0;
  const releaseRestoredCamera = (): void => {
    keepRestoredCamera = false;
  };
  function restoreCamera(s: CameraSnapshot): void {
    controls.setState({
      targetKm: [s.targetKm[0], s.targetKm[1], s.targetKm[2]],
      distanceKm: s.distanceKm,
      orientation: quatNormalize(s.orientation),
    });
    keepRestoredCamera = true;
    for (const type of ['pointerdown', 'wheel', 'keydown', 'touchstart']) {
      window.addEventListener(type, releaseRestoredCamera, { once: true, capture: true });
    }
    window.setTimeout(releaseRestoredCamera, 20_000);
    if (s.following) resumeFollowUntilMs = Date.now() + 20_000;
  }

  // View switches run one after the other (each chained synchronously on the previous one, so two quick
  // clicks cannot run together); a switch superseded by a later request before it starts is skipped.
  let switching = Promise.resolve();
  let requestedView: ViewId = viewId;
  let firstView = true;
  async function switchView(id: ViewId, viewParams?: URLSearchParams): Promise<void> {
    requestedView = id;
    const run = switching.then(async () => {
      if (id !== requestedView) return;
      follow.stop();
      view?.dispose();
      view = undefined;
      for (const el of viewDom) el.remove();
      viewDom = [];
      host.setPanelToggle(undefined);
      showNotice(undefined);
      // The page's URL parameters (selection, filters…) apply to the first view only.
      const params = viewParams ?? (firstView && id === url.view ? startParams : new URLSearchParams());
      firstView = false;
      viewId = id;
      updateCanonical();
      toolbar.setView(id);
      const factory = await VIEW_LOADERS[id]().catch((err: unknown) => {
        // A tab opened before a deployment asks for chunks that no longer exist: reload on the new build (once).
        if (claimAutoReload()) {
          syncUrl();
          window.location.reload();
        }
        throw err;
      });
      view = factory({ ...host, initialParams: params });
      view.setObserver?.(observer);
      controls.setLimits(cameraLimits(view.limits));
      timeControl.setMaxRate(view.maxRate ?? MAX_ABS_RATE, view.maxRateHint ? i18n.t(view.maxRateHint) : '');
      const home = homeState(clock.nowUtc());
      controls.setHome(home);
      controls.setState(home);
      const restored = takeCamera(id, frame);
      if (restored) restoreCamera(restored);
      syncUrl();
    });
    switching = run.catch((err: unknown) => console.error(err));
    await run;
  }
  // ?follow=1: follow the selection once it has a position (as after an update reload).
  if (startParams.get('follow') === '1') resumeFollowUntilMs = Date.now() + 20_000;
  void switchView(viewId).then(() => embedApi?.ready());

  // Clicks: select (click) and follow (double-click); drags are not clicks.
  let downAt: { x: number; y: number } | undefined;
  renderer.canvas.addEventListener('pointerdown', (e) => (downAt = { x: e.clientX, y: e.clientY }));
  const handleClick = (e: MouseEvent, double: boolean): void => {
    if (!view || !downAt) return;
    if (Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y) > CLICK_TOLERANCE_PX) return;
    // Pick against the current camera state, not the last rendered frame (slow GPUs, fresh setState).
    controls.applyTo(renderer.camera);
    view.placeOrigin(controls.cameraPositionKm);
    const rect = renderer.canvas.getBoundingClientRect();
    view.click(e.clientX - rect.left, e.clientY - rect.top, double);
  };
  // F: follow the selection / stop following (same guards as the camera keys; by position, like them).
  window.addEventListener('keydown', (e) => {
    const target = e.target as HTMLElement | null;
    if (target && (target.isContentEditable || ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)))
      return;
    if (e.ctrlKey || e.metaKey || e.altKey || letterCode(e) !== 'KeyF') return;
    e.preventDefault();
    view?.toggleFollow();
  });
  renderer.canvas.addEventListener('click', (e) => handleClick(e, false));
  renderer.canvas.addEventListener('dblclick', (e) => handleClick(e, true));

  // ?debug=perf: tier, reasons, GPU, frame rate and render scale (maintenance only, not translated).
  let framesDrawn = 0;
  if (startParams.get('debug') === 'perf') {
    const overlay = h('pre', { class: 'perf-overlay', 'aria-hidden': 'true' });
    app.append(overlay);
    let last = performance.now();
    window.setInterval(() => {
      const now = performance.now();
      const fps = (framesDrawn * 1000) / (now - last);
      framesDrawn = 0;
      last = now;
      overlay.textContent = `${qualitySummary()}
${
  q.tier === qualityState.detected.tier
    ? ''
    : `detected ${qualityState.detected.tier} (${qualityState.detected.reasons.join(', ')})
`
}${fps.toFixed(0)} fps · DPR ${window.devicePixelRatio} · max texture ${qualityState.signals.maxTextureSize} · MSAA ${qualityState.signals.maxSamples} · ${qualityState.signals.deviceMemoryGb ?? '?'} GB · ${qualityState.signals.cores ?? '?'} cores`;
    }, 500);
  }

  // Photo mode: the 3D view as shown (labels included, panels and bars left out), then the same frame with
  // the bodies and models only. Named after the followed object (else the view's body) and the displayed
  // date, in the display time zone.
  let photoBusy = false;
  function capturePhoto(): { title: string; shown: HTMLCanvasElement; objects: HTMLCanvasElement } {
    const followed = follow.active
      ? app.querySelector('.info:not([hidden]) .panel-title')?.textContent?.trim()
      : undefined;
    const title = i18n.format('photo.title', {
      brand: i18n.t('app.brand'),
      subject: followed || i18n.t(VIEW_LABELS[viewId]),
      date: i18n.dateTime(clock.nowUtc(), true),
    });
    renderer.renderNow();
    const shown = copyCanvas(renderer.canvas);
    drawLabels(shown, app, renderer.canvas.getBoundingClientRect());
    const restore = hideOverlays(renderer.scene);
    try {
      renderer.renderNow();
    } finally {
      restore();
    }
    return { title, shown, objects: copyCanvas(renderer.canvas) };
  }
  async function takePhoto(): Promise<void> {
    if (photoBusy || !view) return;
    photoBusy = true;
    try {
      const { title, shown, objects } = capturePhoto();
      await downloadPng(shown, title);
      await downloadPng(objects, `${title} ${i18n.t('photo.objectsOnly')}`);
      showToast(i18n.t('photo.saved'));
    } catch (err) {
      console.error(err);
      showToast(i18n.t('photo.failed'));
    } finally {
      photoBusy = false;
    }
  }

  /**
   * Moves the app to a Perigee link (a favorite): its date (or real time), rate and frame, then its view and
   * selection. In the same view with the same filters, the object is selected in place and the camera glides to
   * it; otherwise the view is rebuilt from the link's parameters, as when the page opens on it.
   */
  async function openLink(query: string): Promise<void> {
    const p = new URLSearchParams(query);
    const state = parseUrlState(query);
    if (state.time) clock.jumpTo(state.time);
    else clock.goLive();
    clock.setRate(state.rate);
    changeFrame(state.frame);
    const sel = p.get('sel') ?? undefined;
    const viewParams = new URLSearchParams(p);
    for (const key of ['view', 'lang', 't', 'rate', 'frame', 'follow']) viewParams.delete(key);
    let inPlace = false;
    if (state.view === viewId && view) {
      const current = new URLSearchParams();
      view.writeUrl(current);
      const wanted = new URLSearchParams(viewParams);
      for (const q of [current, wanted]) {
        q.delete('sel');
        q.sort();
      }
      if (current.toString() === wanted.toString()) {
        // One frame first, so the view has its objects at the new date before framing one.
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        inPlace = view.selectId(sel);
      }
    }
    if (!inPlace) await switchView(state.view, viewParams);
    if (p.get('follow') === '1' && sel) {
      // Follow the link's object: a follow still running (the previous object, in place) would end the resume.
      if (controls.following) {
        controls.setFollow(undefined);
        endFollow();
      }
      resumeFollowUntilMs = Date.now() + 20_000;
    } else follow.stop();
    timeControl.update();
    syncUrl();
  }

  // Embed API: only when framed by another page.
  if (window.parent !== window) {
    const embedState = (): EmbedState => {
      const p = new URLSearchParams();
      view?.writeUrl(p);
      return {
        view: viewId,
        sel: p.get('sel'),
        time: clock.nowUtc().toISOString(),
        live: clock.isLive(),
        rate: clock.rate,
        frame,
        lang: i18n.lang,
        following: follow.active,
        url: fullAppUrl(),
      };
    };
    embedApi = new EmbedApi(
      {
        state: embedState,
        async set(params: SetParams) {
          if (params.lang && params.lang !== i18n.lang) {
            explicitLang = params.lang;
            i18n.setLang(params.lang);
          }
          if (params.time === 'now') clock.goLive();
          else if (params.time) clock.jumpTo(new Date(params.time));
          if (params.rate !== undefined) clock.setRate(params.rate);
          if (params.frame) changeFrame(params.frame);
          const targetView = params.view ?? viewId;
          if (targetView !== viewId || params.sel !== undefined) {
            const sel = params.sel ?? undefined;
            // Same view: select in place; another view, or an object it does not know: build the view with it.
            if (targetView !== viewId || !view?.selectId(sel)) {
              const p = new URLSearchParams();
              if (sel) p.set('sel', sel);
              await switchView(targetView, p);
            }
          }
          if (params.observer !== undefined) {
            observer = params.observer ?? undefined;
            view?.setObserver?.(observer);
          }
          if (params.follow === false) follow.stop();
          if (params.follow) resumeFollowUntilMs = Date.now() + 20_000;
          timeControl.update();
          syncUrl();
        },
        async snapshot(kind) {
          if (!view) throw new Error('The view is not ready');
          if (photoBusy) throw new Error('A snapshot is already being taken');
          photoBusy = true;
          try {
            const shot = capturePhoto();
            return { title: shot.title, dataUrl: shot[kind].toDataURL('image/png') };
          } finally {
            photoBusy = false;
          }
        },
        async details(state) {
          if (!state.sel) return undefined;
          const e = await getEngine();
          const id = engineIdOf(state.view, state.sel);
          const [facts, position] = await Promise.all([
            e.describe(id, state.lang),
            e.state(id, new Date(state.time)).catch((err: unknown) => ({ error: String(err) })),
          ]);
          return { id, facts, position };
        },
      },
      window.parent,
    );
  }

  // Frame loop
  let uiTimerS = 0;
  let wasFollowing = false;
  renderer.start((dtS) => {
    framesDrawn++;
    const nowMs = clock.nowMs();
    const date = new Date(nowMs);
    // Before the view: animations that follow the clock (clouds) may render off-screen passes here.
    setSimTime(nowMs);
    const v = view;
    if (v) {
      const bodyQ = v.bodyOrientation(date);
      const sceneQ = frame === 'fixed' ? quatConjugate(bodyQ) : QUAT_IDENTITY;
      stars.quaternion.set(sceneQ.x, sceneQ.y, sceneQ.z, sceneQ.w);
      sky.mesh.quaternion.copy(stars.quaternion);
      if (frame === 'inertial') controls.setHome(homeState(date));

      // The view first: the camera may follow one of its objects.
      const frameInfo = {
        nowMs,
        dtS,
        rate: clock.rate,
        clockEpoch: clock.epoch,
        frame,
        bodyQ,
        sceneFromInertial: sceneQ,
      };
      v.update(frameInfo);
      controls.update(dtS);
      // The R key resets the controls directly; restore the view limits when following ends that way.
      if (wasFollowing && !controls.following) endFollow();
      wasFollowing = controls.following;
      // Never inside a planet, moon or the Sun: the camera slides along the surface instead.
      const obstacles = v.cameraObstacles?.() ?? [];
      // A pan (Alt + drag) slides along a body by translation; a rotation or a zoom re-aims round the pivot.
      const panned = controls.takePanned();
      if (obstacles.length) {
        const keep = panned ? keepOutsideByTranslation : keepOutside;
        controls.constrain(keep(controls.state, obstacles, CAMERA_CLEARANCE_KM));
      }
      controls.applyTo(renderer.camera);
      const originKm: Vec3 = controls.cameraPositionKm;
      v.placeOrigin(originKm);
      guardian.update(originKm, controls.limits.boundsHalfKm);
      // Height above the nearest drawn surface, for the near clipping plane.
      const altitudeKm = obstacles.length
        ? Math.min(...obstacles.map((o) => heightAboveKm(originKm, o)))
        : length(originKm) - v.bodyRadiusKm;
      const otherSurfaceKm = v.nearestSurfaceKm?.(originKm) ?? Infinity;
      renderer.setClipPlanes(
        Math.max(
          0.001,
          Math.min(
            Math.max(altitudeKm, 0.01) * 0.5,
            controls.state.distanceKm * 0.1,
            Math.max(otherSurfaceKm, 0.01) * 0.5,
          ),
        ),
        length(originKm) + v.farKm,
      );
      const refKm = altimeterRefKm();
      altimeter.update({
        valueKm: Math.max(1e-6, controls.state.distanceKm - refKm),
        minKm: controls.limits.minDistanceKm - refKm,
        maxKm: controls.limits.maxDistanceKm - refKm,
        kind: refKm === 0 ? 'distance' : 'altitude',
        toScale: v.distancesToScale?.() ?? true,
      });
    }

    uiTimerS += dtS;
    if (uiTimerS >= UI_REFRESH_S) {
      uiTimerS = 0;
      updateBottomInset();
      timeControl.update();
      v?.uiTick(nowMs);
      if (resumeFollowUntilMs > 0) {
        if (follow.active || Date.now() > resumeFollowUntilMs) resumeFollowUntilMs = 0;
        else v?.toggleFollow();
      }
    }
  });
}

main();
